import { useState } from 'react'
import { X, Search, Calendar } from 'lucide-react'
import { cn } from '../atoms'

interface AdvancedSearchModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (query: string) => void
  initialValue?: string
}

export function AdvancedSearchModal({ isOpen, onClose, onSubmit, initialValue = '' }: AdvancedSearchModalProps) {
  const [form, setForm] = useState({
    from: '',
    to: '',
    subject: '',
    hasAttachment: false,
    hasAlert: false,
    hasCase: false,
    afterDate: '',
    beforeDate: '',
    freeText: '',
  })

  if (!isOpen) return null

  const handleSearch = () => {
    const parts: string[] = []
    if (form.from) parts.push(`from:${form.from}`)
    if (form.to) parts.push(`to:${form.to}`)
    if (form.subject) parts.push(`subject:${form.subject}`)
    if (form.hasAttachment) parts.push('has:attachment')
    if (form.hasAlert) parts.push('has:alert')
    if (form.hasCase) parts.push('has:case')
    if (form.afterDate) parts.push(`after:${form.afterDate}`)
    if (form.beforeDate) parts.push(`before:${form.beforeDate}`)
    if (form.freeText) parts.push(form.freeText)
    
    onSubmit(parts.join(' '))
    onClose()
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-900 w-full max-w-lg rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Búsqueda avanzada</h2>
          <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">De</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Ej: dian@gov.co"
                value={form.from}
                onChange={e => setForm({ ...form, from: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">Para</label>
              <input
                type="text"
                className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
                placeholder="Ej: cliente@mail.com"
                value={form.to}
                onChange={e => setForm({ ...form, to: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">Asunto</label>
            <input
              type="text"
              className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
              placeholder="Palabras clave en el asunto"
              value={form.subject}
              onChange={e => setForm({ ...form, subject: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">Contiene palabras</label>
            <input
              type="text"
              className="w-full h-9 px-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
              placeholder="Búsqueda general en todo el email"
              value={form.freeText}
              onChange={e => setForm({ ...form, freeText: e.target.value })}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">Desde</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="date"
                  className="w-full h-9 pl-8 pr-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
                  value={form.afterDate}
                  onChange={e => setForm({ ...form, afterDate: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 ml-1">Hasta</label>
              <div className="relative">
                <Calendar className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-gray-400" />
                <input
                  type="date"
                  className="w-full h-9 pl-8 pr-3 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-primary-500 outline-none"
                  value={form.beforeDate}
                  onChange={e => setForm({ ...form, beforeDate: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 py-2">
            {[
              { id: 'hasAttachment', label: 'Adjuntos', val: 'hasAttachment' },
              { id: 'hasAlert', label: 'Alertas', val: 'hasAlert' },
              { id: 'hasCase', label: 'Tiene Caso', val: 'hasCase' },
            ].map(opt => (
              <label key={opt.id} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={(form as any)[opt.val]}
                  onChange={e => setForm({ ...form, [opt.val]: e.target.checked })}
                />
                <span className="text-sm text-gray-600 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                  {opt.label}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSearch}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors shadow-sm"
          >
            <Search className="w-4 h-4" /> Buscar
          </button>
        </div>
      </div>
    </div>
  )
}
